import React, { useState } from "react";
import ClientLayout from "../../components/client/ClientLayout";
import RequireClientAdmin from "../../components/client/RequireClientAdmin";
import { clientApi } from "../../api";

/**
 * Bulk points-award page — promotional campaigns. Admin pastes or
 * uploads a member-number list, sets amount + optional reason, backend
 * awards in serial with per-member error surfacing.
 */
export default function BulkPointsAward() {
  return (
    <ClientLayout>
      <RequireClientAdmin>
        <Inner />
      </RequireClientAdmin>
    </ClientLayout>
  );
}

function Inner() {
  const [raw, setRaw] = useState("");
  const [amount, setAmount] = useState("");
  const [pointType, setPointType] = useState("BONUS");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const ids = parseIds(raw);

  async function submit() {
    if (!ids.length) return alert("Enter at least one membership number.");
    if (!amount || Number(amount) <= 0) return alert("Points must be positive.");
    if (!window.confirm(`Award ${amount} ${pointType} points to ${ids.length} member(s)?`)) return;
    setBusy(true); setResult(null);
    try {
      const r = await clientApi.bulkAwardPoints({
        members: ids,
        points: Number(amount),
        pointType,
        reason,
      });
      setResult(r);
    } catch (err) { alert(err.message); }
    setBusy(false);
  }

  return (
    <>
      <div className="venues-page-header">
        <div>
          <h1>Bulk Points Award</h1>
          <p className="page-subtitle">Run a promotional award across many members at once</p>
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 2fr) minmax(0, 1fr)", gap: "1rem" }}>
        <div className="settings-card">
          <div className="settings-card-header">
            <h3>Member numbers</h3>
            <p>One per line, or comma separated. {ids.length} will be awarded.</p>
          </div>
          <textarea
            value={raw}
            onChange={(e) => setRaw(e.target.value)}
            placeholder={"56153\n56154\n56155"}
            rows={16}
            style={{ width: "100%", fontFamily: "monospace", padding: "0.5rem", borderRadius: 8, border: "1px solid var(--border)", background: "var(--bg-input)", color: "var(--text)" }}
          />
        </div>

        <div className="settings-card">
          <div className="settings-card-header"><h3>Award</h3><p>Applied to every member above</p></div>
          <div className="form-field">
            <label>Points per member <span className="required">*</span></label>
            <input type="number" min="1" value={amount} onChange={(e) => setAmount(e.target.value)} />
          </div>
          <div className="form-field">
            <label>Point type</label>
            <input value={pointType} onChange={(e) => setPointType(e.target.value)} placeholder="BONUS" />
          </div>
          <div className="form-field">
            <label>Reason (logged per-member)</label>
            <input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. Easter promo" />
          </div>
          <button className="btn btn-primary btn-block" disabled={busy || !ids.length} onClick={submit}>
            {busy ? `Awarding ${ids.length}…` : `Award ${ids.length} member(s)`}
          </button>
        </div>
      </div>

      {result && (
        <div className="settings-card" style={{ marginTop: "1rem" }}>
          <div className="settings-card-header">
            <h3>Result</h3>
            <p>{result.awarded} awarded · {result.failed} failed · {result.total} total</p>
          </div>
          {!!result.errors?.length && (
            <div className="table-wrap">
              <table className="data-table">
                <thead><tr><th>Membership</th><th>Error</th></tr></thead>
                <tbody>
                  {result.errors.map((e, i) => (
                    <tr key={i}><td><code>#{e.membershipId}</code></td><td style={{ fontSize: "0.85rem", color: "var(--danger)" }}>{e.error}</td></tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </>
  );
}

function parseIds(raw) {
  return String(raw)
    .split(/[\s,]+/)
    .map((x) => x.trim())
    .filter(Boolean)
    .filter((x) => /^\d+$/.test(x));
}
